import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import { Box, Button, CircularProgress, Grid, LinearProgress, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

// import { record } from '@aws-amplify/analytics';
import CustomBackdrop from '../../../../components/CustomBackdrop/CustomBackdrop';
import { useValidation } from '../../../../contexts/ValidationContext';
import {
  CompanyType,
  useGetCompanyQuery,
  useGetOpenaiVotesQuery,
  useMarkCompanyAsNotSoftwareMutation,
  useStartVerificationSessionMutation,
  useGetSuggestedKeywordsQuery,
  useSaveValidationKeywordMutation,
  useGetUserIdByAuthIdQuery,
} from '../../../../gql/generated/graphql';
import { getFirstNode } from '../../../../gql/helpers';
import { ensureProtocol } from '../../../../helpers/helpers';
import { paths } from '../../../../routes/paths';
import CustomLink from '../../../DFMPage/components/CustomLink/CustomLink';
import ValidationReasonSelect from '../../../DFMPage/components/ValidationReasonSelect';

import { isSoftwareStepStyles } from './styles';

type Props = {
  companyId: number;
};

const IsSoftwareStep = ({ companyId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyValidation, setCompanyValidation } = useValidation();
  const { user } = useAuthenticator();

  const { data: openaiVotes } = useGetOpenaiVotesQuery({
    variables: { companyId },
  });

  const { data: company, loading: companyLoading } = useGetCompanyQuery({
    variables: { companyId },
  });

  // Fetch suggested keywords for validation reasoning
  const { data: suggestedKeywordsData, loading: keywordsLoading } = useGetSuggestedKeywordsQuery({
    variables: { companyId },
    skip: !companyId,
  });

  const [markCompanyAsNotSoftware, { loading: markCompanyAsNotSoftwareLoading }] =
    useMarkCompanyAsNotSoftwareMutation();

  const [saveValidationKeyword] = useSaveValidationKeywordMutation();
  const [startVerificationSession] = useStartVerificationSessionMutation();

  const companyNode: CompanyType = getFirstNode(company?.getCompany);
  const openaiVotesNode = getFirstNode(openaiVotes?.getOpenaiVotes);

  const authId = user.username.replace('GoogleWorkspace_', '');

  // Get the numeric user ID from authId
  const { data: userIdData } = useGetUserIdByAuthIdQuery({
    variables: { authId },
    skip: !authId,
  });

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    console.log('authId', authId);
    // This is Helge fudging: If the session cannot be started, we jump back to the inbox
    const result = await startVerificationSession({ variables: { companyId, authId } });
    if (result?.data?.startCompanyVerificationSession?.success)
      setCompanyValidation({ isSoftware: event.target.value });
    else navigate(paths.crawlerInbox);
    // record({
    //   // this still fails due to the missing app id
    //   name: 'validation-started',
    // });
  };

  const handleMarkAsNotSoftware = async () => {
    const result = await markCompanyAsNotSoftware({ variables: { companyId } });
    if (!result?.data?.markCompanyAsNotSoftware?.ok) return;
    navigate(paths.crawlerInbox); // We need to display an error message here
  };

  const handleKeywordSelected = async (keyword: string, source: string, confidence: number) => {
    try {
      // Get the numeric user ID from the query result
      const userId = userIdData?.userIdByAuthId;

      if (!userId) {
        console.error('User ID not found for authId:', authId);
        return;
      }

      const isSoftware = companyValidation?.isSoftware === 'yes';
      await saveValidationKeyword({
        variables: {
          companyId,
          userId,
          keyword,
          source,
          confidence,
          isSoftware,
        },
      });
      console.log('Keyword saved:', keyword, source, confidence, 'isSoftware:', isSoftware);
    } catch (error) {
      console.error('Error saving validation keyword:', error);
    }
  };

  const percentagesicCodeCertainty = (companyNode?.sicCodeCertainty * 100).toFixed(0) + '%';
  const isNotSoftware = companyValidation?.isSoftware === 'no';

  if (companyLoading)
    return (
      <Box textAlign="center">
        <CircularProgress />
      </Box>
    );

  return (
    <>
      <Box>
        {/* Header row with title only */}
        <Box mb={2}>
          <Typography variant="h6" component="h2">
            Is this a software company?
          </Typography>
        </Box>

        {/* First row: Is software company */}
        <Grid container spacing={2} alignItems="center" mb={3}>
          <Grid item xs={12}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="is-software"
                name="is-software-company-group"
                value={companyValidation?.isSoftware}
                onChange={handleChange}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

        {/* Keyword selection - shown after user makes a decision */}
        {companyValidation?.isSoftware && (
          <Box mb={3}>
            <ValidationReasonSelect
              companyId={companyId}
              isSoftware={companyValidation.isSoftware === 'yes'}
              onKeywordSelected={handleKeywordSelected}
              suggestedKeywords={suggestedKeywordsData?.getSuggestedKeywords || null}
              loading={keywordsLoading}
            />
          </Box>
        )}

        {/* Second row: SIC Certainty bar */}
        {(companyNode?.sicCodeCertainty ?? 0) >= 0 && (
          <Box mb={3}>
            <Typography variant="body2" fontWeight="medium" color="primary" mb={1}>
              SIC Certainty
            </Typography>
            <LinearProgress
              variant="determinate"
              value={companyNode?.sicCodeCertainty * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="body2" textAlign="end" mt={0.5} color="text.secondary">
              {percentagesicCodeCertainty}
            </Typography>
          </Box>
        )}

        {/* Third row: Rationale */}
        {companyNode?.sicCodeJustification && (
          <Box mb={3}>
            <Typography variant="body2" fontWeight="medium" color="primary" mb={1}>
              Rationale
            </Typography>
            <Box
              sx={{
                p: 2.5,
                backgroundColor: 'action.hover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {companyNode.sicCodeJustification}
              </Typography>
            </Box>
          </Box>
        )}

        {isNotSoftware && (
          <Button onClick={handleMarkAsNotSoftware} variant="contained" sx={{ mt: 2 }}>
            {t('validation.markAsNotSoftwareButton')}
          </Button>
        )}
      </Box>

      <Box mt={3} sx={isSoftwareStepStyles.iframeBox}>
        <iframe
          width="100%"
          height="100%"
          title="validated company website"
          src={ensureProtocol(companyNode?.uniqueDomain)}
          sandbox="allow-scripts allow-same-origin allow-forms"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </Box>
      <CustomBackdrop open={markCompanyAsNotSoftwareLoading} />
    </>
  );
};

export default IsSoftwareStep;
